
import {  motion, useMotionTemplate, useMotionValue } from "framer-motion";
import { ArrowRight, Globe, Zap, CreditCard, Users, } from "lucide-react";
import React from "react";


const Hero = () => {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
 
  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] as const }
  };

  const targetUsers = [
    "Creators", "Merchants", "Startups", 
    "Developer teams", "Web3 communities", "Enterprises"
  ];

  return (
    <div 
      onMouseMove={handleMouseMove}
      // Đổi nền sang màu trắng xanh nhạt như trong ảnh
      className="group relative min-h-screen bg-[#F8F9FF] text-[#1A1A1A] overflow-hidden scrollbar-hide"
    >
      {/* 1. Nâng cấp Spotlight Background - Chuyển sang màu xanh dương nhạt */}
      <motion.div
        className="pointer-events-none absolute -inset-px opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(59, 130, 246, 0.08),
              transparent 80%
            )
          `,
        }}
      />

      {/* Grid Pattern ngầm - Chỉnh màu line sang màu tối nhẹ */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#5865F208_1px,transparent_1px),linear-gradient(to_bottom,#5865F208_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]"></div>

      <div className="container max-w-7xl mx-auto px-4 py-16 text-center sm:px-6 md:py-24 lg:px-8 lg:py-32 pt-32 pb-20 relative z-10">
        <div className="flex flex-col items-center text-center">
          
          {/* Badge - Chuyển sang phong cách Light Mode */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="px-4 py-1.5 mb-8 rounded-full border border-indigo-100  bg-indigo-50/50 backdrop-blur-md text-[10px] font-bold tracking-[0.2em] uppercase text-blue-600 shadow-sm"
          >
             • Protocol-Aware Business OS
          </motion.div>

          {/* Tiêu đề - Đậm và tương phản hơn */}
          <motion.h1 
            {...fadeInUp}
            className="text-5xl md:text-8xl tracking-tighter mb-8 leading-[1.1] text-[#0A0A0A]"
          >
            The Operating Layer <br />
            <motion.span 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="bg-clip-text text-transparent bg-gradient-to-b from-blue-600 to-indigo-900"
            >
              of the Gao Internet
            </motion.span>
          </motion.h1>

          <motion.p 
            {...fadeInUp}
            transition={{ delay: 0.2 }}
            className="max-w-3xl text-lg md:text-xl text-slate-600 mb-12 leading-relaxed"
          >
            Gao Workspace is a  
            <span className="text-blue-600 font-semibold"> domain-native</span> operational infrastructure that integrates  <span className="text-blue-600 font-semibold">identity, payments, governance, and artificial intelligence</span> into a unified system. 
            Each  <span className="text-blue-600 font-semibold"> .gao </span> domain functions as a programmable organizational environment.
          </motion.p>

          {/* 2. Nâng cấp Interaction cho Buttons - Chỉnh màu theo ảnh mẫu */}
          <motion.div 
            {...fadeInUp}
            transition={{ delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 mb-20"
          >
            <motion.button 
              onClick={() => window.location.href = "https://gao.domains/en"}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 bg-[#0A0A0A] text-white rounded-2xl font-bold transition-all flex items-center justify-center gap-2 group shadow-xl hover:bg-[#222]"
            >
               Deploy .gao Identity
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button 
              onClick={() => window.location.href = "https://toii-network.gitbook.io/gao-internet"}
              whileHover={{ scale: 1.02, backgroundColor: "#fff" }}
              className="px-8 py-4 bg-white border border-slate-600 text-slate-900 rounded-2xl font-bold shadow-sm hover:shadow-md transition-all"
            >
              Technical Documentation 
            </motion.button>
          </motion.div>

          {/* 3. Nâng cấp Card UX - Chuyển sang màu trắng (White Mode) */}
        

            <motion.div 
  initial="initial"
  whileInView="animate"
  viewport={{ once: true }}
  className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full max-w-6xl mb-24"
>
  {[
    { label: "Identity", sub: "Domain", icon: <Globe size={24}/>, color: "text-blue-600", bg: "bg-blue-50/80", shadow: "shadow-blue-200/20" },
    { label: "Operations", sub: "Workspace", icon: <Zap size={24}/>, color: "text-indigo-600", bg: "bg-indigo-50/80", shadow: "shadow-indigo-200/20" },
    { label: "Revenue", sub: "Payment", icon: <CreditCard size={24}/>, color: "text-emerald-600", bg: "bg-emerald-50/80", shadow: "shadow-emerald-200/20" },
    { label: "Automation", sub: "AI", icon: <Users size={24}/>, color: "text-orange-600", bg: "bg-orange-50/80", shadow: "shadow-orange-200/20" }
  ].map((item, i) => (
    <motion.div 
      key={i}
      whileHover={{ 
        y: -12,
        transition: { duration: 0.4, ease: [0.23, 1, 0.32, 1] }
      }}
      className="group relative p-10 rounded-[2.5rem] bg-white border border-neutral-100/80 shadow-[0_8px_30px_rgb(0,0,0,0.02)] transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.08)]"
    >
  
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 blur-[100px] transition-opacity duration-700 rounded-[2.5rem] -z-10 ${item.bg}`} />

   
      <div className={`relative mb-8 w-14 h-14 ${item.bg} ${item.shadow} rounded-[1.25rem] flex items-center justify-center transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 backdrop-blur-sm`}>
        <div className={`${item.color} transition-transform duration-500 group-hover:scale-110`}>
          {item.icon}
        </div>
        

        <div className="absolute inset-0 rounded-[1.25rem] border border-white/40 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
     
      <div className="text-left relative z-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-2 font-black font-mono flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${item.color.replace('text', 'bg')} opacity-50`} />
          {item.sub}
        </div>
        <div className="text-2xl font-bold text-slate-900 tracking-tight leading-tight group-hover:translate-x-1 transition-transform duration-300">
          {item.label}
        </div>
      </div>

      <div className="mt-6 overflow-hidden">
        <div className="h-[1px] w-0 bg-neutral-100 group-hover:w-full transition-all duration-700" />
        <div className="mt-4 flex items-center justify-between opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 delay-100">
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active System</span>
          <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${item.color.replace('text', 'bg')}`} />
        </div>
      </div>

    
      <div className="absolute top-6 right-6 p-2 opacity-0 group-hover:opacity-100 transition-all duration-500">
        <div className={`w-2 h-2 rounded-full border-2 ${item.color.replace('text', 'border')} bg-transparent`} />
      </div>
    </motion.div>
  ))}
</motion.div>  
         
        
<div className="grid grid-cols-1 md:grid-cols-2 gap-12 w-full max-w-5xl text-left border-t border-slate-100 pt-20">
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.5, duration: 0.8 }}
  >
    <h3 className="text-xs font-black uppercase tracking-[0.25em] text-blue-600/80 mb-8 flex items-center gap-3">
      <span className="w-8 h-[2px] bg-blue-600/20"></span>
      Core capabilities include:
    </h3>
    <div className="grid grid-cols-1 gap-5">
      {[
         "Domain-native identity architecture",
         "Role-based access governance",
         "Integrated payment infrastructure",
         "AI-driven workflow orchestration",
         "Compliance-ready audit systems",
      ].map((p, i) => (
        <motion.div 
          key={i} 
          whileHover={{ x: 5 }}
          className="flex items-center gap-4 text-base text-slate-600 font-medium group cursor-default"
        >
          <div className="flex-shrink-0 w-6 h-6 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-blue-600 transition-colors duration-300">
             <div className="w-1.5 h-1.5 rounded-full bg-blue-600 group-hover:bg-white transition-colors"></div>
          </div>
          <span className="group-hover:text-slate-900 transition-colors">{p}</span>
        </motion.div>
      ))}
    </div>
  </motion.div>

  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.6, duration: 0.8 }}
  >
    <h3 className="text-xs font-black uppercase tracking-[0.25em] text-indigo-600/80 mb-8 flex items-center gap-3">
      <span className="w-8 h-[2px] bg-indigo-600/20"></span>
      Target Ecosystem
    </h3>
    <div className="flex flex-wrap gap-3">
      {targetUsers.map((user, i) => (
        <motion.span 
          key={i} 
          whileHover={{ 
            scale: 1.05, 
            backgroundColor: "#fff",
            borderColor: "rgba(99, 102, 241, 0.4)" 
          }}
          className="px-5 py-2.5 rounded-2xl bg-slate-50 border border-slate-500 text-slate-600 text-xs font-bold tracking-tight shadow-sm cursor-default transition-all duration-300 hover:text-indigo-600 hover:shadow-md"
        >
          {user}
        </motion.span>
      ))}
    </div>
  </motion.div>
</div>
        </div>
      </div>

      {/* Marquee - Chỉnh độ mờ và màu chữ tối hơn một chút */}
      <div className="absolute bottom-6 left-0 w-full overflow-hidden whitespace-nowrap opacity-[0.05] pointer-events-none">
        <div className="flex gap-20 animate-marquee text-[12rem] font-black uppercase tracking-tighter text-blue-900">
          <span>GAO WORKSPACE</span>
          <span>BUSINESS OS</span>
          <span>GAO WORKSPACE</span>
        </div>
      </div>    
        <style>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 60s linear infinite;
          display: inline-flex;
          width: max-content;
        }
      `}</style>
    </div>
  );
};

export default Hero;
